<template>
    <div class="detail-page">
        <file-preview ref="filePreview" download-base-url="/web/dataValue/download" />
        <!-- 基本信息 -->
        <el-card class="box-card" shadow="never">
            <div slot="header" class="clearfix">
                <span>企业及资产信息</span>
            </div>
            <el-descriptions :column="3" border size="small" v-if="detail">
                <el-descriptions-item label="资产登记编号">{{ detail.registrationId }}</el-descriptions-item>
                <el-descriptions-item label="资产名称">{{ detail.assetName }}</el-descriptions-item>
                <el-descriptions-item label="企业名称">{{ detail.companyName }}</el-descriptions-item>
                <el-descriptions-item label="当前状态">
                    <el-tag v-if="detail.state === '待评估'" type="warning" size="small">待评估</el-tag>
                    <el-tag v-else-if="detail.state === '评估完成'" type="success" size="small">评估完成</el-tag>
                    <el-tag v-else type="info" size="small">{{ detail.state }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="申请时间">{{ formatDateTime(detail.createTime) }}</el-descriptions-item>
            </el-descriptions>
        </el-card>

        <!-- 提交材料 -->
        <el-card class="box-card" shadow="never" style="margin-top:16px;">
            <div slot="header" class="clearfix">
                <span>提交材料</span>
            </div>
            <div v-if="detail">
                <!-- 必传材料 -->
                <div class="section-label">必传材料</div>
                <el-table :data="requiredMaterials" border style="width:100%"
                    :header-cell-style="{ background: '#f5f5f5' }">
                    <el-table-column label="材料名称" width="220" prop="label"></el-table-column>
                    <el-table-column label="上传文件">
                        <template slot-scope="scope">
                            <div v-if="detail[scope.row.key] && detail[scope.row.key].length">
                                <div v-for="(url, idx) in detail[scope.row.key]" :key="idx"
                                    style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
                                    <i class="el-icon-document" style="color:#409EFF;"></i>
                                    <span style="flex:1; color:#303133; font-size:13px;">{{ getFileName(url) }}</span>
                                    <el-button type="text" size="mini"
                                        v-if="isPreviewable(getFileName(url))"
                                        @click="previewFile(url, detail.registrationId)">预览</el-button>
                                    <el-button type="text" size="mini"
                                        @click="downloadFile(url, detail.registrationId)">下载</el-button>
                                </div>
                            </div>
                            <span v-else style="color:#C0C4CC;">未上传</span>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 选填材料 -->
                <div class="section-label" style="margin-top:20px;">选填材料</div>
                <el-table :data="optionalMaterials" border style="width:100%"
                    :header-cell-style="{ background: '#f5f5f5' }">
                    <el-table-column label="材料名称" width="220" prop="label"></el-table-column>
                    <el-table-column label="上传文件">
                        <template slot-scope="scope">
                            <div v-if="detail[scope.row.key] && detail[scope.row.key].length">
                                <div v-for="(url, idx) in detail[scope.row.key]" :key="idx"
                                    style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
                                    <i class="el-icon-document" style="color:#409EFF;"></i>
                                    <span style="flex:1; color:#303133; font-size:13px;">{{ getFileName(url) }}</span>
                                    <el-button type="text" size="mini"
                                        v-if="isPreviewable(getFileName(url))"
                                        @click="previewFile(url, detail.registrationId)">预览</el-button>
                                    <el-button type="text" size="mini"
                                        @click="downloadFile(url, detail.registrationId)">下载</el-button>
                                </div>
                            </div>
                            <span v-else style="color:#C0C4CC;">未上传</span>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
        </el-card>

        <!-- 评估操作区（待评估时显示上传报告） -->
        <el-card class="box-card" shadow="never" style="margin-top:16px;"
            v-if="detail && detail.state !== '评估完成'">
            <div slot="header" class="clearfix">
                <span>上传价值评估报告</span>
            </div>
            <el-row :gutter="24">
                <el-col :span="12">
                    <el-upload drag action="#" :auto-upload="false"
                        :on-change="handleReportFileChange"
                        :file-list="reportFileList" :limit="1"
                        accept=".pdf">
                        <i class="el-icon-upload"></i>
                        <div class="el-upload__text">
                            点击或拖拽评估报告到此处<br>
                            <span style="color:gray; font-size:12px;">仅支持 PDF，不超过100M</span>
                        </div>
                    </el-upload>
                </el-col>
                <el-col :span="12">
                    <div style="font-size:13px; color:#606266; margin-bottom:8px;">评估意见（选填）</div>
                    <el-input type="textarea" :rows="5" v-model="reviewComments"
                        placeholder="请输入评估意见..."></el-input>
                </el-col>
            </el-row>
            <div style="margin-top:20px; text-align:right;">
                <el-button @click="close('/ValueAssessment')" style="width:80px;">返回</el-button>
                <el-button type="primary" @click="submitReview"
                    :disabled="!reportFileList.length || isReviewing"
                    :loading="isReviewing" style="width:130px; margin-left:12px;">
                    {{ isReviewing ? '提交中...' : '提交评估结果' }}
                </el-button>
            </div>
        </el-card>

        <!-- 自动生成评估报告 -->
        <el-card class="box-card" shadow="never" style="margin-top:16px;"
            v-if="detail && detail.state !== '评估完成'">
            <div slot="header" class="clearfix">
                <span>自动生成价值评估报告</span>
            </div>
            <el-row :gutter="24" type="flex" align="middle">
                <el-col :span="8">
                    <div style="font-size:13px; color:#606266; margin-bottom:8px;">
                        评估价值（万元）<span style="color:red;">*</span>
                    </div>
                    <el-input v-model="valuationAmount" placeholder="请输入估值，如：500"
                        style="width:100%;" clearable>
                        <template slot="append">万元</template>
                    </el-input>
                </el-col>
                <el-col :span="16">
                    <div style="font-size:12px; color:#909399; line-height:1.8; padding-top:22px;">
                        系统将根据填写的估值自动生成标准格式的数据价值评估报告（Word文档），
                        生成后可直接下载，下载后可手动上传至审批区提交评估结果。
                    </div>
                </el-col>
            </el-row>
            <div style="margin-top:20px; text-align:right;">
                <el-button type="success" @click="handleGenerateReport"
                    :disabled="!valuationAmount || isGenerating"
                    :loading="isGenerating" style="width:160px;">
                    {{ isGenerating ? '生成中...' : '生成评估报告' }}
                </el-button>
            </div>
            <div v-if="generatedFileName" style="margin-top:16px; padding:12px 16px; background:#f0f9eb; border-radius:4px; border:1px solid #e1f3d8;">
                <span style="font-size:13px; color:#67C23A; margin-right:12px;">✓ 报告已生成：{{ generatedFileName }}</span>
                <el-button type="text" size="small" @click="downloadFile(generatedFileName, registrationId)"
                    style="color:#409EFF;">点击下载</el-button>
            </div>
        </el-card>

        <!-- 生成进度弹窗 -->
        <el-dialog title="AI正在生成评估报告" :visible.sync="progressDialogVisible"
            width="480px" :close-on-click-modal="false" :close-on-press-escape="false"
            :show-close="false">
            <div style="padding: 10px 0;">
                <div style="font-size:13px; color:#606266; margin-bottom:16px; text-align:center;">
                    {{ progressStepLabel }}
                </div>
                <el-progress
                    :percentage="progressPercent"
                    :color="progressPercent === 100 ? '#67C23A' : '#409EFF'"
                    :stroke-width="16"
                    :format="p => p + '%'"
                />
                <div style="margin-top:12px; text-align:center; font-size:12px; color:#909399;">
                    {{ progressStep }} / 9 步完成，请勿关闭页面...
                </div>
            </div>
        </el-dialog>

        <!-- 自动生成价值评估报告初稿 -->
        <el-card class="box-card" shadow="never" style="margin-top:16px;"
            v-if="detail && detail.state !== '评估完成'">
            <div slot="header" class="clearfix">
                <span>自动生成价值评估报告初稿</span>
            </div>
            <el-row :gutter="24" type="flex" align="middle">
                <el-col :span="24">
                    <div v-if="generatedValuationAmount" style="margin-bottom:16px; padding:12px 16px; background:#ecf5ff; border-radius:4px; border:1px solid #d9ecff;">
                        <span style="font-size:13px; color:#409EFF;">
                            <i class="el-icon-info"></i> 评估价值：<strong>{{ generatedValuationAmount }} 万元</strong>
                            （来自上方生成的评估报告）
                        </span>
                    </div>
                    <div style="font-size:12px; color:#909399; line-height:1.8;">
                        系统将自动读取已上传材料，通过AI提取关键信息，生成标准格式的初稿Word文档。
                        评估价值部分以【待填写】占位，需人工核实后填入。生成完成后可直接下载。
                    </div>
                </el-col>
            </el-row>
            <div style="margin-top:20px; text-align:right;">
                <el-button type="warning" @click="handleGenerateDraft"
                    :disabled="isDraftGenerating"
                    :loading="isDraftGenerating" style="width:160px;">
                    {{ isDraftGenerating ? '生成中...' : '生成初稿报告' }}
                </el-button>
            </div>
            <div v-if="draftFileName" style="margin-top:16px; padding:12px 16px; background:#fdf6ec; border-radius:4px; border:1px solid #faecd8;">
                <span style="font-size:13px; color:#E6A23C; margin-right:12px;">✓ 初稿已生成：{{ draftFileName }}</span>
                <el-button type="text" size="small" @click="downloadFile(draftFileName, registrationId)"
                    style="color:#409EFF;">点击下载</el-button>
            </div>
        </el-card>

        <!-- 初稿生成进度弹窗 -->
        <el-dialog title="AI正在生成评估报告初稿" :visible.sync="draftProgressDialogVisible"
            width="480px" :close-on-click-modal="false" :close-on-press-escape="false"
            :show-close="false">
            <div style="padding: 10px 0;">
                <div style="font-size:13px; color:#606266; margin-bottom:16px; text-align:center;">
                    {{ draftProgressStepLabel }}
                </div>
                <el-progress
                    :percentage="draftProgressPercent"
                    :color="draftProgressPercent === 100 ? '#67C23A' : '#E6A23C'"
                    :stroke-width="16"
                    :format="p => p + '%'"
                />
                <div style="margin-top:12px; text-align:center; font-size:12px; color:#909399;">
                    {{ draftProgressStep }} / 5 步完成，请勿关闭页面...
                </div>
            </div>
        </el-dialog>

        <!-- 已完成时展示结果 -->
        <el-card class="box-card" shadow="never" style="margin-top:16px;"
            v-if="detail && detail.state === '评估完成'">
            <div slot="header" class="clearfix">
                <span>评估结果</span>
            </div>
            <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="评估报告">
                    <el-button v-if="detail.valueAssessmentReport" type="text"
                        @click="downloadFile(detail.valueAssessmentReport, detail.registrationId)">
                        <i class="el-icon-document"></i> {{ detail.valueAssessmentReport }}
                    </el-button>
                    <span v-else style="color:#C0C4CC;">-</span>
                </el-descriptions-item>
                <el-descriptions-item label="评估意见">{{ detail.reviewComments || '-' }}</el-descriptions-item>
                <el-descriptions-item label="评估完成时间">{{ detail.reviewTime || '-' }}</el-descriptions-item>
            </el-descriptions>
            <div style="margin-top:20px; text-align:right;">
                <el-button @click="close('/ValueAssessment')" style="width:80px;">返回</el-button>
            </div>
        </el-card>
    </div>
</template>

<script>
import FilePreview from '@/components/FilePreview.vue'

export default {
    name: 'ValueAssessmentDetail',
    components: { FilePreview },
    data() {
        return {
            registrationId: '',
            userId: '',
            detail: null,
            reportFileList: [],
            reviewComments: '',
            isReviewing: false,
            valuationAmount: '',
            isGenerating: false,
            generatedFileName: '',
            progressDialogVisible: false,
            progressStep: 0,
            progressTimer: null,
            isDraftGenerating: false,
            draftFileName: '',
            draftProgressDialogVisible: false,
            draftProgressStep: 0,
            draftProgressTimer: null,
            generatedValuationAmount: '',
            requiredMaterials: [
                { key: 'businessLicense',   label: '营业执照' },
                { key: 'assetCertificate',  label: '数据资产证书' },
                { key: 'qualityReport',     label: '质量评价报告' },
                { key: 'legalOpinion',      label: '合规法律意见书' },
                { key: 'companyProfile',    label: '公司简介' },
                { key: 'interviewRecord',   label: '访谈记录' },
                { key: 'auditReport3year',  label: '近三年审计报告或财务报表' },
                { key: 'auditReport3month', label: '近三个月审计报告或财务报表' },
                { key: 'companyCharter',    label: '公司章程' },
                { key: 'assetChecklist',    label: '数据资产查验表' }
            ],
            optionalMaterials: [
                { key: 'salesContract',        label: '数据销售合同' },
                { key: 'feasibilityReport',    label: '可行性研究报告（数据相关）' },
                { key: 'intellectualProperty', label: '知识产权相关（专利、软著）' },
                { key: 'otherMaterials',       label: '其他补充材料' }
            ]
        }
    },
    created() {
        this.registrationId = this.$route.query.registrationId || '';
        this.userId = this.$route.query.userId || '';
        this.loadDetail();
    },
    beforeDestroy() {
        if (this.progressTimer) {
            clearInterval(this.progressTimer);
            this.progressTimer = null;
        }
        if (this.draftProgressTimer) {
            clearInterval(this.draftProgressTimer);
            this.draftProgressTimer = null;
        }
    },
    computed: {
        progressPercent() {
            return Math.round(this.progressStep / 9 * 100);
        },
        draftProgressPercent() {
            return Math.round(this.draftProgressStep / 5 * 100);
        },
        draftProgressStepLabel() {
            const labels = [
                '初始化中...',
                'AI执行中：提取信息并生成摘要、委托人、评估对象章节...',
                'AI执行中：生成评估方法、评估程序章节...',
                'AI执行中：生成评估假设、特别事项章节...',
                '正在写入Word文档...',
                '初稿生成完成！'
            ];
            return labels[Math.min(this.draftProgressStep, 5)] || '处理中...';
        },
        progressStepLabel() {
            const labels = [
                '初始化中...',
                'AI提取材料信息...',
                '生成评估报告摘要...',
                '生成委托人与产权持有人概述...',
                '生成评估对象与范围...',
                '生成评估方法论证...',
                '生成收益预测说明...',
                '生成评估程序...',
                '生成评估假设...',
                '生成特别事项说明，正在写入文档...'
            ];
            return labels[Math.min(this.progressStep, 9)] || '处理中...';
        }
    },
    methods: {
        async loadDetail() {
            const resp = await this.$axios.get('/web/dataValue/detail', {
                params: { registrationId: this.registrationId }
            });
            if (resp.data && resp.data.status === 200) {
                this.detail = resp.data.object;
                this.draftFileName = (resp.data.object && resp.data.object.draftFileName) || '';
            } else {
                this.$message.error('获取详情失败');
            }
        },
        handleReportFileChange(file, fileList) {
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                this.$message.error('评估报告只支持 PDF 格式');
                fileList.splice(fileList.indexOf(file), 1);
                return;
            }
            this.reportFileList = fileList.slice(-1);
        },
        async submitReview() {
            if (!this.reportFileList.length) {
                this.$message.warning('请先上传评估报告');
                return;
            }
            this.isReviewing = true;
            const loadingInst = this.$loading({
                lock: true, text: '正在上传评估报告...', background: 'rgba(0,0,0,0.7)'
            });
            try {
                const formData = new FormData();
                formData.append('file', this.reportFileList[0].raw);
                formData.append('registrationId', this.registrationId);
                formData.append('type', 'valueAssessmentReport');
                const uploadResp = await this.$axios.post('/web/dataValue/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (!uploadResp.data || uploadResp.data.status !== 200) {
                    throw new Error(uploadResp.data.msg || '上传失败');
                }
                const reviewResp = await this.$axios.post('/web/dataValue/review', {
                    registrationId: this.registrationId,
                    valueAssessmentReport: uploadResp.data.object,
                    reviewComments: this.reviewComments,
                    result: 'complete'
                }, { headers: { 'X-User-Id': this.userId } });

                loadingInst.close();
                if (reviewResp.data && reviewResp.data.status === 200) {
                    this.$message.success('评估提交成功');
                    this.close('/ValueAssessment');
                } else {
                    this.$message.error('提交失败: ' + (reviewResp.data.msg || '未知错误'));
                }
            } catch (err) {
                loadingInst.close();
                this.$message.error('操作失败: ' + (err.message || '未知错误'));
            } finally {
                this.isReviewing = false;
            }
        },
        getFileName(url) {
            if (!url) return '';
            return url.split('/').pop();
        },
        getFileExtension(filename) {
            if (!filename) return '';
            return filename.split('.').pop().toLowerCase();
        },
        isPreviewable(filename) {
            const ext = this.getFileExtension(filename);
            const previewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf',
                'xls', 'xlsx', 'csv', 'doc', 'docx', 'txt', 'json', 'xml', 'log', 'js', 'css', 'html', 'md'];
            return previewable.includes(ext);
        },
        async downloadFile(urlOrName, registrationId) {
            const fileName = this.getFileName(urlOrName);
            try {
                const response = await this.$axios({
                    method: 'get',
                    url: '/web/dataValue/download',
                    params: { registrationId, fileName },
                    responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (err) {
                this.$message.error('下载失败: ' + err.message);
            }
        },
        async previewFile(urlOrName, registrationId) {
            const fileName = this.getFileName(urlOrName);
            this.$refs.filePreview.openPreview({ fileName }, registrationId);
        },
        async handleGenerateReport() {
            if (!this.valuationAmount) {
                this.$message.warning('请先输入评估价值');
                return;
            }
            this.isGenerating = true;
            this.progressStep = 0;
            this.progressDialogVisible = true;

            try {
                // 触发异步生成（立即返回）
                await this.$axios.post('/web/dataValue/generateReport', null, {
                    params: {
                        registrationId: this.registrationId,
                        valuationAmount: this.valuationAmount
                    }
                });
            } catch (err) {
                this.progressDialogVisible = false;
                this.isGenerating = false;
                this.$message.error('启动生成失败: ' + (err.message || '未知错误'));
                return;
            }

            // 每2秒轮询进度
            this.progressTimer = setInterval(async () => {
                try {
                    const resp = await this.$axios.get('/web/dataValue/generateProgress', {
                        params: { registrationId: this.registrationId }
                    });
                    if (resp.data && resp.data.status === 200) {
                        const step = resp.data.object;
                        if (step === -1) {
                            // 生成失败
                            clearInterval(this.progressTimer);
                            this.progressTimer = null;
                            this.progressDialogVisible = false;
                            this.isGenerating = false;
                            this.$message.error('报告生成失败，请重试');
                        } else {
                            this.progressStep = step;
                            if (step >= 9) {
                                // 生成完成，只刷新报告文件名，不影响审批状态
                                clearInterval(this.progressTimer);
                                this.progressTimer = null;
                                this.progressDialogVisible = false;
                                this.isGenerating = false;
                                // 只拉文件名，不替换 detail（保持审批状态不变）
                                try {
                                    const detailResp = await this.$axios.get('/web/dataValue/detail', {
                                        params: { registrationId: this.registrationId }
                                    });
                                    if (detailResp.data && detailResp.data.object) {
                                        this.generatedFileName = detailResp.data.object.valueAssessmentReport || '';
                                        // 只更新文件名字段，不覆盖 detail.state 等审批相关字段
                                        if (this.detail) {
                                            this.detail.valueAssessmentReport = this.generatedFileName;
                                        }
                                        // 获取后端计算的精确评估金额（带小数）
                                        if (detailResp.data.object.totalValue) {
                                            this.generatedValuationAmount = detailResp.data.object.totalValue.toString();
                                        }
                                    }
                                } catch (e) { /* ignore */ }
                                this.$message.success('评估报告生成成功，可点击下载');
                            }
                        }
                    }
                } catch (err) {
                    // 轮询失败不中断，继续等待
                }
            }, 2000);
        },
        async handleGenerateDraft() {
            this.isDraftGenerating = true;
            this.draftProgressStep = 0;
            this.draftProgressDialogVisible = true;
            try {
                await this.$axios.post('/web/dataValue/generateDraft', null, {
                    params: {
                        registrationId: this.registrationId,
                        valuationAmount: this.generatedValuationAmount || ''
                    }
                });
            } catch (err) {
                this.draftProgressDialogVisible = false;
                this.isDraftGenerating = false;
                this.$message.error('启动初稿生成失败: ' + (err.message || '未知错误'));
                return;
            }
            this.draftProgressTimer = setInterval(async () => {
                try {
                    const resp = await this.$axios.get('/web/dataValue/getDraftProgress', {
                        params: { registrationId: this.registrationId }
                    });
                    if (resp.data && resp.data.status === 200) {
                        const step = resp.data.object;
                        if (step === -1) {
                            clearInterval(this.draftProgressTimer);
                            this.draftProgressTimer = null;
                            this.draftProgressDialogVisible = false;
                            this.isDraftGenerating = false;
                            this.$message.error('初稿生成失败，请重试');
                        } else {
                            this.draftProgressStep = step;
                            if (step >= 5) {
                                clearInterval(this.draftProgressTimer);
                                this.draftProgressTimer = null;
                                this.draftProgressDialogVisible = false;
                                this.isDraftGenerating = false;
                                try {
                                    const detailResp = await this.$axios.get('/web/dataValue/detail', {
                                        params: { registrationId: this.registrationId }
                                    });
                                    if (detailResp.data && detailResp.data.object) {
                                        this.draftFileName = detailResp.data.object.draftFileName || '';
                                    }
                                } catch (e) { /* ignore */ }
                                this.$message.success('评估报告初稿生成成功，可点击下载');
                            }
                        }
                    }
                } catch (err) { /* 轮询失败不中断 */ }
            }, 2000);
        },
        close(targetPage = '/ValueAssessment') {
            const fullPath = this.$route.fullPath;
            const currentPath = this.$route.path;
            if (currentPath !== targetPage) {
                this.$router.push(targetPage).then(() => {
                    setTimeout(() => this.closeTab(fullPath), 100);
                }).catch(() => this.closeTab(fullPath));
            } else {
                this.closeTab(fullPath);
            }
        },
        closeTab(fullPath) {
            this.$emit('close-tab', fullPath);
            let parent = this.$parent;
            while (parent) {
                if (typeof parent.removeTab === 'function') {
                    parent.removeTab(fullPath);
                    break;
                }
                parent = parent.$parent;
            }
        },
        formatDateTime(dateTime) {
            if (!dateTime) return '-';
            const date = new Date(dateTime);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
    }
}
</script>

<style scoped>
.detail-page {
    padding: 16px;
    background-color: #f5f7fa;
    min-height: 100%;
}

.box-card {
    border-radius: 8px;
}

.box-card >>> .el-card__header {
    background-color: #f0f2f5;
    font-weight: bold;
    font-size: 15px;
}

.section-label {
    font-size: 13px;
    font-weight: bold;
    color: #606266;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid #409EFF;
}

.clearfix:before, .clearfix:after { display: table; content: ""; }
.clearfix:after { clear: both; }
</style>
